import { ClientCartDetail, ICartItem } from "common/types/carts";
import { Optional } from "common/utils/types";

import { CartRefillTooSoonSummary } from "client/carts/types";

import { SubscriptionOptionType, OptionType, CheckoutStep } from "../types";

export const getSubscriptionCartItems = (cartItems: ICartItem[]) => {
  return cartItems.filter((cartItem) => !!cartItem.subscription);
};

export const optionTypeComplete = (
  options: OptionType | SubscriptionOptionType,
  cartItems: ICartItem[],
) => !cartItems.some((cartItem) => !options[cartItem.id]);

export const subscriptionOptionTypeComplete = (
  options: SubscriptionOptionType,
  cartItems: ICartItem[],
) => optionTypeComplete(options, getSubscriptionCartItems(cartItems));

export const steps = [CheckoutStep.Items, CheckoutStep.Delivery, CheckoutStep.Payment];

export const validators: {
  [K in CheckoutStep]: (
    cart: ClientCartDetail,
    refillTooSoonSummary: CartRefillTooSoonSummary,
  ) => boolean;
} = {
  items: (cart, refillTooSoonSummary) =>
    !cart.missing_required_client_fields_message &&
    cart.items.every(
      ({
        patient,
        missing_required_patient_fields_message: missingFieldsMessage,
        is_available: isAvailable,
        flavor_selection,
        item,
      }) =>
        !!patient &&
        !missingFieldsMessage &&
        isAvailable &&
        // either have a selection, or item doesn't need selections
        (!item.flavor_options.length || !!flavor_selection),
    ) &&
    refillTooSoonSummary.cart_items.every(
      ({ is_valid_by_refill_too_soon: isValidByRTS }) => isValidByRTS,
    ),
  delivery: (cart) => cart.is_delivery_info_complete,
  payment: (cart) => !!cart.payment_method,
};

export const inferStep = (
  cart: ClientCartDetail,
  refillTooSoonSummary: CartRefillTooSoonSummary,
) => {
  if (cart.is_closed) {
    return null;
  }

  if (!validators.items(cart, refillTooSoonSummary)) {
    return CheckoutStep.Items;
  }

  if (!validators.delivery(cart, refillTooSoonSummary)) {
    return CheckoutStep.Delivery;
  }

  return CheckoutStep.Payment;
};

export const isValidStep = (
  inferredStep: CheckoutStep,
  step: Optional<string>,
): step is CheckoutStep => {
  const inferredStepIndex = steps.indexOf(inferredStep);
  // cast is `CartStep` because `indexOf` should be safe for any type, but resolve to -1 if not valid
  const stepIndex = steps.indexOf(step as CheckoutStep);
  return inferredStepIndex !== -1 && stepIndex !== -1 && stepIndex <= inferredStepIndex;
};
